import { defineStore } from 'pinia'
import { useAlertsStore } from '@/store/pinia/alerts'
import { useOrganizationStore } from '@/store/pinia/organization'
import { ObjWithPrivateProps, RepoUrl } from '@/utils/classes'
import { makeResetStoreAction, makeSetErrorsAction, makeClearErrorsAction, makeStartFetchAction, makeStopFetchAction } from '@/utils/helpers/pinia'
import i18n from '@/utils/plugins/i18n'

export const state = () => ({
  detail: null,
  branches: null,
  errors: {
    branches: [],
    catalogRepository: [],
    refresh: [],
    refreshVersions: [],
  },
  fetchInProgress: {
    catalogRepository: false,
    refreshVersions: false,
  },
})

export const useCatalogRepositoryStore = defineStore('catalogRepository', {
  state,
  getters: {
    catalogRepository: (state) => state.detail,
    hasBranches: (state) => !_.isEmpty(state.branches),
    hasBranchesErrors: (state) => !_.isEmpty(state.errors.branches),
  },
  actions: {
    /**
     * Clear the current errors list.
     */
    CLEAR_CR_ERRORS: makeClearErrorsAction(state),

    /**
     * Reset the whole module state to its initial shape.
     */
    RESET_CR_STATE: makeResetStoreAction(state),

    /**
     * Replace the current errors array.
     */
    SET_ERRORS: makeSetErrorsAction(),

    /**
     * Start fetch operation.
     */
    START_FETCH: makeStartFetchAction(),

    /**
     * Stop fetch operation.
     */
    STOP_FETCH: makeStopFetchAction(),

    /**
     * Get owner object.
     * @param {Object} params
     * @param {Object} params.data
     * @param {Object} params.oldVal
     * @returns {Object}
     */
    GET_OWNER_OBJECT ({ data, oldVal }) {
      return data
    },

    /**
     * Replace the current catalog repository object in state.
     * @param {Object} repo
     * @returns {void}
     */
    SET_CATALOG_REPOSITORY (repo) {
      const catalogRepository = _.cloneDeep(repo)
      catalogRepository.service_catalogs = _.sortBy(repo.service_catalogs, ['canonical'])

      this.detail = new ObjWithPrivateProps(catalogRepository, {
        _repoURL: new RepoUrl(catalogRepository),
      })
    },

    /**
     * Set branches in state.
     * @param {Array} branches
     * @returns {void}
     */
    SET_BRANCHES (branches) {
      this.branches = branches
    },

    /**
     * Set stacks in state.
     * @param {Array} stacks
     * @returns {void}
     */
    SET_STACKS (stacks) {
      if (!this.detail) return
      const existingStacks = this.detail.service_catalogs || []
      const mergedStacks = _.unionBy(existingStacks, stacks, 'canonical')
      this.detail.stack_count = mergedStacks.length
      this.detail.service_catalogs = _.sortBy(mergedStacks, 'canonical')
    },

    /**
     * Get catalog repository by canonical.
     * @param {Object} params
     * @param {string} params.catalogRepositoryCanonical
     * @returns {Promise<void>}
     */
    async GET_CATALOG_REPOSITORY ({ catalogRepositoryCanonical }) {
      const orgStore = useOrganizationStore()

      this.START_FETCH('catalogRepository')
      this.CLEAR_CR_ERRORS('catalogRepository')

      const { data, errors } = (await this.$cycloid.ydAPI.getServiceCatalogSource(
        orgStore.orgCanonical,
        catalogRepositoryCanonical,
      )) || {}

      if (data) this.SET_CATALOG_REPOSITORY(data)
      if (errors) this.SET_ERRORS({ key: 'catalogRepository', errors })

      this.STOP_FETCH('catalogRepository')
    },

    /**
     * Create a new catalog repository.
     * @param {Object} params
     * @param {Object} params.catalogRepository
     * @param {VueRouter} [params.$router]
     * @returns {Promise<void>}
     */
    async CREATE_CATALOG_REPOSITORY ({ catalogRepository, $router }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_CR_ERRORS('catalogRepository')

      const { data, errors } = (await this.$cycloid.ydAPI.createServiceCatalogSource(
        orgStore.orgCanonical,
        catalogRepository,
      )) || {}

      if (data) {
        this.SET_CATALOG_REPOSITORY(data)
        if ($router) $router.replace({ name: 'catalogRepository', params: { catalogRepositoryCanonical: data.canonical } })
        const content = i18n.global.t('alerts.success.catalogRepository.created', { catalogRepoName: data.name })
        alertStore.SHOW_ALERT({ type: 'success', content })
      }

      if (errors) this.SET_ERRORS({ key: 'catalogRepository', errors })
    },

    /**
     * Update an existing catalog repository.
     * @param {Object} params
     * @param {Object} params.catalogRepository
     * @param {string} [params.successMessage]
     * @returns {Promise<void>}
     */
    async UPDATE_CATALOG_REPOSITORY ({ catalogRepository, successMessage }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_CR_ERRORS('catalogRepository')

      const { data, errors } = (await this.$cycloid.ydAPI.updateServiceCatalogSource(
        orgStore.orgCanonical,
        catalogRepository,
      )) || {}

      if (data) {
        this.SET_CATALOG_REPOSITORY(await this.GET_OWNER_OBJECT({ data, oldVal: catalogRepository }))
        const content = successMessage || i18n.global.t('alerts.success.catalogRepository.updated', { catalogRepoName: data.name })
        alertStore.SHOW_ALERT({ type: 'success', content })
      }

      if (errors) this.SET_ERRORS({ key: 'catalogRepository', errors })
    },

    /**
     * Delete a catalog repository.
     * @param {Object} params
     * @param {Object} params.catalogRepository
     * @param {VueRouter} [params.$router]
     * @returns {Promise<void>}
     */
    async DELETE_CATALOG_REPOSITORY ({ catalogRepository, $router }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.CLEAR_CR_ERRORS('catalogRepository')

      const { errors } = (await this.$cycloid.ydAPI.deleteServiceCatalogSource(
        orgStore.orgCanonical,
        catalogRepository.canonical,
      )) || {}

      if (errors) {
        this.SET_ERRORS({ key: 'catalogRepository', errors })
        return
      }

      const content = i18n.global.t('alerts.success.catalogRepository.deleted', { catalogRepoName: catalogRepository.name })
      alertStore.SHOW_ALERT({ type: 'success', content })
      if ($router) $router.replace({ name: 'catalogRepositories' })
    },

    /**
     * Validate a catalog repository.
     * @param {Object} params
     * @param {string} params.catalogRepositoryCanonical
     * @returns {Promise<void>}
     */
    async VALIDATE_CATALOG_REPOSITORY ({ catalogRepositoryCanonical }) {
      const orgStore = useOrganizationStore()

      this.CLEAR_CR_ERRORS('catalogRepository')

      const { errors } = (await this.$cycloid.ydAPI.validateServiceCatalogSource(
        orgStore.orgCanonical,
        catalogRepositoryCanonical,
      )) || {}

      if (errors) this.SET_ERRORS({ key: 'catalogRepository', errors })
    },

    /**
     * Get repository branches.
     * @param {Object} params
     * @param {string} params.credentialCanonical
     * @param {string} params.url
     * @returns {Promise<void>}
     */
    async GET_BRANCHES_REPOSITORY ({ credentialCanonical, url }) {
      const orgStore = useOrganizationStore()
      const code = 'InvalidCatalogRepositoryNoBranchesFound'

      const { data, errors } = (await this.$cycloid.ydAPI.getRepoBranches(
        orgStore.orgCanonical,
        credentialCanonical,
        url,
      )) || {}

      if (data) {
        this.CLEAR_CR_ERRORS('catalogRepository')
        this.CLEAR_CR_ERRORS('branches')
        this.SET_BRANCHES(data.sort())
      }

      if (_.every([data, errors], _.isEmpty)) {
        this.SET_ERRORS({ key: 'catalogRepository', errors: [{ code, message: i18n.global.t(`apiErrors.${code}`) }] })
      }

      if (errors) this.SET_ERRORS({ key: 'branches', errors })
    },

    /**
     * Refresh catalog repository.
     * @param {Object} params
     * @param {string} params.canonical
     * @param {string} params.name
     * @returns {Promise<Object|void>}
     */
    async REFRESH_CATALOG_REPOSITORY ({ canonical, name }) {
      const orgStore = useOrganizationStore()

      this.CLEAR_CR_ERRORS('refresh')

      const { data, errors } = (await this.$cycloid.ydAPI.refreshServiceCatalogSource(
        orgStore.orgCanonical,
        canonical,
      )) || {}

      if (data) {
        const stacksAfterDeletion = _.differenceBy(
          this.detail?.service_catalogs || [],
          data.deleted || [],
          'canonical',
        )
        const stacksAfterUpdateAndCreation = _.uniqWith(
          [...(data.updated || []), ...(data.created || [])], _.isEqual,
        )

        const stacks = (_.sortBy(
          _.unionBy(
            stacksAfterDeletion,
            stacksAfterUpdateAndCreation,
            'canonical',
          ),
          'canonical',
        )).map((stack) => ({
          ...stack,
          cloudProviders: stack.cloud_providers || [],
          keywords: stack.keywords || [],
        }))

        orgStore.SET_AVAILABLE({ key: 'stacks', value: stacks })
        this.SET_STACKS(_.uniqWith([...(data.updated || []), ...(data.created || [])], _.isEqual))
        return data
      }

      if (errors) {
        return {
          errored: errors.map((e) => ({ message: e.message })),
        }
      }
    },

    /**
     * Refresh catalog repository versions.
     * @param {Object} params
     * @param {string} params.canonical
     * @param {string} params.name
     * @returns {Promise<Object|void>}
     */
    async REFRESH_CATALOG_REPOSITORY_VERSIONS ({ canonical, name }) {
      const orgStore = useOrganizationStore()
      const alertStore = useAlertsStore()

      this.START_FETCH('refreshVersions')
      this.CLEAR_CR_ERRORS('refreshVersions')

      const { data, errors } = await this.$cycloid.ydAPI.refreshServiceCatalogSourceVersions(
        orgStore.orgCanonical,
        canonical,
      ) || {}

      if (data) {
        const content = i18n.global.t('alerts.success.catalogRepository.refreshed', { catalogRepoName: name })
        orgStore.SET_AVAILABLE({ key: 'catalogRepositoryVersions', value: data })
        alertStore.SHOW_ALERT({ type: 'success', content })
        this.STOP_FETCH('refreshVersions')
        return data
      }

      if (errors) {
        if (this.detail) {
          this.SET_ERRORS({ key: 'refreshVersions', errors })
        } else {
          errors[0].message = i18n.global.t('alerts.errors.catalogRepository.refresh', { name, errorMessage: errors[0].message })
          alertStore.SHOW_ALERT({ type: 'error', content: errors })
        }
      }

      this.STOP_FETCH('refreshVersions')
    },
  },
})
